import { useRouter } from "next/router";
import { Auth } from "src/domains/shared/utils/amplifyCompat";
import { queryClient } from "src/pages/_app";
import { releaseLogin } from "../api/exclusiveLogin/exclusiveLogin.repository";
import { SIGNED_OUT_ROUTE } from "../utils/exclusiveLogin/loginRoutes";
import { clearLoginSessionSource } from "../utils/exclusiveLogin/loginSessionSource";
import { writeLoginTabSignal } from "../utils/exclusiveLogin/loginTabSignal";
import { holdLoginSignOutFetchStop } from "../utils/exclusiveLogin/exclusiveLoginFetchGate";

/** 로그아웃 흐름의 결과를 받는 자리 — 반환 함수는 흐름이 실패해도 reject 하지 않고 `onFailed` 로 알린다. */
interface SignOutHandlers {
  /** 로컬 로그아웃 직후·화면 이동 직전 — 호출자의 로그인 상태 비우기 자리. */
  onSignedOut?: () => void;
  /** 어느 단계든 실패했을 때 — 실패한 단계 뒤의 단계는 건너뛴다. 이 핸들러 자신이 던진 것만 반환 Promise 의 reject 로 나간다. */
  onFailed?: (error: unknown) => void;
}

/**
 * 명시적 로그아웃 흐름 — `useAuth` 의 로그인 상태(GetUser 호출·기기 충돌 창·로케일)를 들지 않는 얇은 훅.
 * 회원 탈퇴(`useWithdrawAccount`)처럼 로그아웃만 이어 가면 되는 소비자가 쓴다. `useAuth().signOut` 은 이 흐름 위에서
 * 자기 로그인 상태를 비운다 — 그 소비자는 헤더(`currentUser` 와 함께 쓴다)와 마이페이지 창(`MyPage` — `useAuth()` 에서
 * `signOut` 만 쓴다)이다.
 *
 * 순서(스펙 「명시적 로그아웃은 전파·해제·로컬 로그아웃 순서로 진행한다」): 다른 탭이 먼저 따르게 하고, 이 탭의 백엔드
 * 요청·스트림을 멈춘 뒤, 토큰이 남아 있는 동안 자리를 해제하고(3초 상한·실패 무시) 로그아웃한다. 이 흐름이 건 정지는
 * 로그아웃이 끝나면(실패해도) 푼다 — 그 사이 다른 탭의 로그아웃 신호로 감시 훅이 건 정지는 감시 훅이 따로 푼다.
 *
 * 반환 함수는 흐름이 실패해도 reject 하지 않는다 — 실패는 `onFailed` 로 넘긴다. `useAuth().signOut`·`signOutThisBrowser`
 * 와 같은 계약이라, `await` 만 하는 호출자가 로그아웃 실패에 걸려 자기 뒷정리를 건너뛰지 않는다.
 */
export const useSignOut = () => {
  const router = useRouter();

  return async (handlers: SignOutHandlers = {}): Promise<void> => {
    try {
      writeLoginTabSignal("signed-out");
      const releaseSignOutStop = holdLoginSignOutFetchStop();
      try {
        await releaseLogin();
        await Auth.signOut();
      } finally {
        releaseSignOutStop();
      }
      clearLoginSessionSource();
      handlers.onSignedOut?.();
      router.replace(SIGNED_OUT_ROUTE);
      queryClient.clear();
    } catch (error) {
      handlers.onFailed?.(error);
    }
  };
};
